import {
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
} from "framer-motion";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import MobileNav from "@/components/mobile-nav";
import { Fragment, ReactNode, useEffect, useState } from "react";
import { InstagramIcon } from "./icons";
import { enWords, Locale } from "@/locales/dict";
import { useIsMobile } from "@/hooks/use-mobile";

type Props = {
  children: ReactNode;
  activePage: string;
  words: (typeof enWords)["navBar"];
  locale: Locale;
  filterComp?: JSX.Element;
};

function Footer() {
  return (
    <footer className="container mx-auto bg-background py-4 border-t border-accentAlt mt-12 px-2">
      <div className="flex justify-between">
        <div className="flex flex-col md:flex-row gap-4">
          {/*<a className="hover:underline" href="">
            Terms of Service
          </a>
          <a className="hover:underline" href="">
            Privacy
            </a>*/}
          <div className="font-abril">
            Copyright © 2025 <span className="font-abril">meraki</span>
          </div>
        </div>
        <div className="flex flex-row gap-2 items-center font-semibold">
          <a target="_blank" href="https://www.instagram.com/meraki.rest/">
            <InstagramIcon className="size-5 fill-primary" />
          </a>
        </div>
      </div>
    </footer>
  );
}

const AppBreadcrumb = ({
  breadcrumbItems,
}: {
  breadcrumbItems: { label: string; href?: string }[];
}) => {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbItems &&
          breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            return (
              <Fragment key={item.label}>
                {!isLast && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink href={item?.href}>
                        {item.label}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                )}
                {isLast && <BreadcrumbPage>{item.label}</BreadcrumbPage>}
              </Fragment>
            );
          })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export function Header(props: {
  activePage: string;
  menuItems: { label: string; href: string }[];
  filterComp?: JSX.Element;
  breadcrumbItems?: { label: string; href?: string }[];
}) {
  const { activePage, menuItems, filterComp, breadcrumbItems } = props;

  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();
  const [lastScrollY, setLastScrollY] = useState(0);

  const isMobile = useIsMobile();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = lastScrollY;
    if (latest > previous && latest > (isMobile ? 128 : 176)) {
      setHidden(true);
    } else if (latest < previous) {
      setHidden(false);
    }
    setLastScrollY(latest);
  });

  return (
    <motion.header
      className="w-full top-0 bg-background z-10 fixed h-28 md:h-40"
      variants={{
        visible: { y: 0 },
        hidden: { y: "-200%" },
      }}
      animate={hidden ? "hidden" : "visible"}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <div className="flex flex-col items-center container mx-auto relative">
        <div className="flex flex-col items-center w-full justify-center">
          <div className="font-abril text-6xl md:text-7xl text-primary">
            meraki
          </div>
          <div className="font-robotomono text-md md:text-lg text-accentAlt font-semibold">
            by Manuel Rodriguez
          </div>
        </div>

        <nav className="hidden md:block text-primary stroke font-semibold">
          <ul data-nosnippet>
            {menuItems.map((i) => {
              return (
                <li
                  key={i.label}
                  className={i.href.endsWith(activePage) ? "active" : ""}
                >
                  <a
                    className="font-extrabold"
                    href={i.href}
                    target={i.href === "/admin/admin-menu" ? "_blank" : "_self"}
                  >
                    {i.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        {filterComp && breadcrumbItems && (
          <div className="bg-background mx-auto container flex flex-row items-center border-b p-2 border-primary">
            <div className="container mx-auto">
              <AppBreadcrumb breadcrumbItems={breadcrumbItems} />
            </div>
            <div className="bottom-0 right-0 w-full flex justify-end">
              {filterComp}
            </div>
          </div>
        )}
        {!filterComp && breadcrumbItems && (
          <div className="bg-background mx-auto container flex flex-row items-center border-b p-2 border-primary">
            <div className="container mx-auto">
              <AppBreadcrumb breadcrumbItems={breadcrumbItems} />
            </div>
          </div>
        )}
        {filterComp && !breadcrumbItems && (
          <div className="absolute -bottom-10 right-0 pr-4 w-full flex justify-end">
            {props.filterComp}
          </div>
        )}
        <MobileNav menuItems={menuItems} activePage={activePage} />
      </div>
    </motion.header>
  );
}

export function ScrollingLine() {
  const { scrollYProgress } = useScroll();
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const checkScrollability = () => {
      const documentHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      setIsScrollable(documentHeight > viewportHeight);
    };

    checkScrollability();

    window.addEventListener("resize", checkScrollability);
    window.addEventListener("load", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
      window.removeEventListener("load", checkScrollability);
    };
  }, []);

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  if (!isScrollable) {
    return null;
  }

  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[5px] bg-primary z-20 origin-left"
    />
  );
}

export default function PublicLayout(props: Props) {
  const { words, activePage, filterComp } = props;
  const menuItems = [
    { label: words.restaurant, href: `/${props.locale}/restaurant` },
    { label: words.menu, href: `/${props.locale}/menu` },
    { label: words.reserve, href: `/${props.locale}/reserve` },
    { label: words.contact, href: `/${props.locale}/contact` },
  ];

  return (
    <>
      <ScrollingLine />
      <Header
        menuItems={menuItems}
        activePage={activePage}
        filterComp={filterComp}
      />
      <div className="grid min-h-screen grid-rows-[1fr,auto] overflow-hidden">
        <main className="container mx-auto mt-28 md:mt-40">
          {props.children}
        </main>
        <Footer />
      </div>
    </>
  );
}
